import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Stage, Layer, Rect, Text, Group } from 'react-konva';
import Konva from 'konva';
import { useEditorStore, SketchRectangle } from '@/stores/editor/editor-store';
import { v4 as uuidv4 } from 'uuid';

interface SketchOverlayProps {
  width: number;
  height: number;
  className?: string;
}

export const SketchOverlay: React.FC<SketchOverlayProps> = ({ width, height, className }) => {
  const stageRef = useRef<Konva.Stage>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [newRect, setNewRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  const {
    sketches,
    selectedSketchId,
    drawingTool,
    isDrawingMode,
    addSketch,
    selectSketch,
    updateSketch,
    deleteSketch,
    setDrawingTool,
  } = useEditorStore();

  // Handle keyboard shortcuts for deleting sketches
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle delete/backspace if user is typing in an input field
      const activeElement = document.activeElement;
      const isTyping = activeElement?.tagName === 'INPUT' ||
                       activeElement?.tagName === 'TEXTAREA' ||
                       (activeElement as HTMLElement)?.contentEditable === 'true';

      if (isTyping) {
        return; // Let the input field handle the key
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedSketchId) {
        deleteSketch(selectedSketchId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedSketchId, deleteSketch]);

  const handleMouseDown = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    if (drawingTool !== 'rectangle' || !isDrawingMode) return;

    const stage = e.target.getStage();
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    setIsDrawing(true);
    setNewRect({ x: pos.x, y: pos.y, width: 0, height: 0 });
    selectSketch(null); // Deselect any selected sketch
  }, [drawingTool, isDrawingMode, selectSketch]);

  const handleMouseMove = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!isDrawing || !newRect) return;

    const stage = e.target.getStage();
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    setNewRect({
      ...newRect,
      width: pos.x - newRect.x,
      height: pos.y - newRect.y
    });
  }, [isDrawing, newRect]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !newRect) return;

    setIsDrawing(false);

    // Only add rectangle if it has a minimum size
    if (Math.abs(newRect.width) > 20 && Math.abs(newRect.height) > 20) {
      const rectangle: SketchRectangle = {
        id: uuidv4(),
        x: newRect.width < 0 ? newRect.x + newRect.width : newRect.x,
        y: newRect.height < 0 ? newRect.y + newRect.height : newRect.y,
        width: Math.abs(newRect.width),
        height: Math.abs(newRect.height),
        stroke: '#3B82F6',
        fill: 'rgba(59, 130, 246, 0.1)',
        selected: false
      };
      addSketch(rectangle);

      // Auto-switch to select mode and select the newly created rectangle
      setDrawingTool('select');
      selectSketch(rectangle.id);
    }

    setNewRect(null);
  }, [isDrawing, newRect, addSketch, setDrawingTool, selectSketch]);

  const handleRectangleClick = useCallback((rectangleId: string) => {
    if (drawingTool !== 'select') return;
    selectSketch(rectangleId);
  }, [drawingTool, selectSketch]);

  const handleStageClick = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    // Check if we clicked on empty space
    if (e.target === e.target.getStage() || e.target.className === 'Layer') {
      if (drawingTool === 'select') {
        selectSketch(null);
      }
    }
  }, [drawingTool, selectSketch]);


  const handleResize = useCallback((rectangleId: string, corner: string, e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const sketch = sketches.find(s => s.id === rectangleId);
    if (!sketch) return;

    const onMouseMove = () => {
      const newPos = stage.getPointerPosition();
      if (!newPos) return;

      let newX = sketch.x;
      let newY = sketch.y;
      let newWidth = sketch.width;
      let newHeight = sketch.height;

      switch (corner) {
        case 'top-left':
          newWidth = sketch.x + sketch.width - newPos.x;
          newHeight = sketch.y + sketch.height - newPos.y;
          newX = newPos.x;
          newY = newPos.y;
          break;
        case 'top-right':
          newWidth = newPos.x - sketch.x;
          newHeight = sketch.y + sketch.height - newPos.y;
          newY = newPos.y;
          break;
        case 'bottom-left':
          newWidth = sketch.x + sketch.width - newPos.x;
          newHeight = newPos.y - sketch.y;
          newX = newPos.x;
          break;
        case 'bottom-right':
          newWidth = newPos.x - sketch.x;
          newHeight = newPos.y - sketch.y;
          break;
      }

      if (newWidth > 20 && newHeight > 20) {
        updateSketch(rectangleId, {
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight
        });
      }
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [sketches, updateSketch]);

  // Don't render if not in drawing mode and no sketches
  if (!isDrawingMode && sketches.length === 0) {
    return null;
  }

  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: width,
        height: height,
        pointerEvents: isDrawingMode || sketches.length > 0 ? 'auto' : 'none',
        zIndex: isDrawingMode ? 999 : 5
      }}
    >
      {/* Drawing Mode Indicator */}
      {isDrawingMode && sketches.length === 0 && (
        <div
          className="absolute top-4 left-4 bg-blue-500/10 border border-blue-500/50 rounded-lg p-3 pointer-events-none"
          style={{ zIndex: 100 }}
        >
          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">Drawing Mode Active</span>
          </div>
          <p className="text-xs text-blue-600/80 dark:text-blue-400/80 mt-1">
            Click and drag to sketch component areas
          </p>
        </div>
      )}

      <Stage
        ref={stageRef}
        width={width}
        height={height}
        onMouseDown={handleMouseDown}
        onMousemove={handleMouseMove}
        onMouseup={handleMouseUp}
        onClick={handleStageClick}
        style={{ cursor: isDrawingMode && drawingTool === 'rectangle' ? 'crosshair' : 'default' }}
      >
        <Layer>
          {/* Existing sketches */}
          {sketches.map((rect) => (
            <Group
              key={rect.id}
              onClick={() => handleRectangleClick(rect.id)}
              onTap={() => handleRectangleClick(rect.id)}
            >
              <Rect
                x={rect.x}
                y={rect.y}
                width={rect.width}
                height={rect.height}
                fill={rect.fill || 'rgba(59, 130, 246, 0.1)'}
                stroke={selectedSketchId === rect.id ? '#1E40AF' : (rect.stroke || '#3B82F6')}
                strokeWidth={selectedSketchId === rect.id ? 3 : 2}
                dash={[8, 4]} // Always dashed to indicate it's a sketch
                cornerRadius={8}
              />

              {/* Annotation label if present */}
              {rect.annotation && (
                <Group>
                  <Rect
                    x={rect.x + 5}
                    y={rect.y + 5}
                    width={Math.min(rect.width - 10, 200)}
                    height={25}
                    fill="white"
                    stroke="#E5E7EB"
                    cornerRadius={4}
                    opacity={0.9}
                  />
                  <Text
                    x={rect.x + 10}
                    y={rect.y + 10}
                    text={rect.annotation.substring(0, 30) + (rect.annotation.length > 30 ? '...' : '')}
                    fontSize={12}
                    fill="#6B7280"
                    width={Math.min(rect.width - 20, 190)}
                  />
                </Group>
              )}

              {/* Selection handles */}
              {selectedSketchId === rect.id && drawingTool === 'select' && !isDrawingMode && (
                <>
                  {/* Corner handles for resizing */}
                  <Rect
                    x={rect.x - 4}
                    y={rect.y - 4}
                    width={8}
                    height={8}
                    fill="#1E40AF"
                    onMouseDown={(e) => {
                      e.cancelBubble = true;
                      handleResize(rect.id, 'top-left', e);
                    }}
                    style={{ cursor: 'nw-resize' }}
                  />
                  <Rect
                    x={rect.x + rect.width - 4}
                    y={rect.y - 4}
                    width={8}
                    height={8}
                    fill="#1E40AF"
                    onMouseDown={(e) => {
                      e.cancelBubble = true;
                      handleResize(rect.id, 'top-right', e);
                    }}
                    style={{ cursor: 'ne-resize' }}
                  />
                  <Rect
                    x={rect.x - 4}
                    y={rect.y + rect.height - 4}
                    width={8}
                    height={8}
                    fill="#1E40AF"
                    onMouseDown={(e) => {
                      e.cancelBubble = true;
                      handleResize(rect.id, 'bottom-left', e);
                    }}
                    style={{ cursor: 'sw-resize' }}
                  />
                  <Rect
                    x={rect.x + rect.width - 4}
                    y={rect.y + rect.height - 4}
                    width={8}
                    height={8}
                    fill="#1E40AF"
                    onMouseDown={(e) => {
                      e.cancelBubble = true;
                      handleResize(rect.id, 'bottom-right', e);
                    }}
                    style={{ cursor: 'se-resize' }}
                  />
                </>
              )}
            </Group>
          ))}

          {/* New rectangle being drawn */}
          {isDrawing && newRect && (
            <Rect
              x={newRect.width < 0 ? newRect.x + newRect.width : newRect.x}
              y={newRect.height < 0 ? newRect.y + newRect.height : newRect.y}
              width={Math.abs(newRect.width)}
              height={Math.abs(newRect.height)}
              fill="rgba(59, 130, 246, 0.1)"
              stroke="#3B82F6"
              strokeWidth={2}
              dash={[8, 4]}
              cornerRadius={8}
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
};
